import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import api from '../lib/api';
import { acceptPendingInvite } from '../lib/pendingInvite';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid or missing token');
      return;
    }

    let active = true;
    const verifyAndLogin = async () => {
      try {
        const response = await api.get<{
          token?: string;
          needsAcceptance?: boolean;
          continuationToken?: string;
        }>('/auth/verify', {
          params: { token },
        });
        const data = response.data;
        if (data?.needsAcceptance && data.continuationToken) {
          if (!active) return;
          navigate(
            `/auth/accept-terms?continuation=${encodeURIComponent(data.continuationToken)}`,
            { replace: true },
          );
          return;
        }
        let accessToken = data?.token;
        if (!accessToken || typeof accessToken !== 'string') {
          setError('Invalid auth response');
          return;
        }
        if (!active) return;

        const upgraded = await acceptPendingInvite(accessToken);
        if (upgraded) accessToken = upgraded;

        login(accessToken);
        navigate('/', { replace: true });
      } catch (err: unknown) {
        if (!active) return;
        if (axios.isAxiosError<{ error?: string }>(err)) {
          setError(err.response?.data?.error || 'Sign in failed');
          return;
        }
        setError('Sign in failed');
      }
    };

    void verifyAndLogin();

    return () => {
      active = false;
    };
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-sm border text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in failed</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-600">Signing you in...</p>
    </div>
  );
}
